export type TradePage = {
  slug: string;
  name: string;
  headline: string;
  intro: string;
  services: string[];
  prices: { label: string; range: string }[];
  tips: string[];
  faqs: { question: string; answer: string }[];
};

// Price ranges are rough Swiss guide values (CHF, incl. VAT) for orientation only.
export const tradePages: TradePage[] = [
  {
    slug: "maler",
    name: "Maler & Gipser",
    headline: "Maler und Gipser in der Schweiz finden",
    intro:
      "Ob frisch gestrichene Wohnung vor dem Auszug, neuer Abrieb oder eine Fassadenrenovation: Beschreiben Sie Ihr Malerprojekt einmal und vergleichen Sie Offerten von Betrieben aus Ihrer Region.",
    services: [
      "Wände und Decken streichen",
      "Fassade streichen",
      "Abrieb und Verputz",
      "Tapezieren",
      "Lackierarbeiten an Türen und Fenstern",
    ],
    prices: [
      { label: "Wände streichen pro m²", range: "CHF 15 bis 35" },
      { label: "3.5-Zimmer-Wohnung komplett", range: "CHF 3'500 bis 7'000" },
      { label: "Fassade streichen pro m²", range: "CHF 30 bis 60" },
    ],
    tips: [
      "Geben Sie Wohnfläche und Raumhöhe an, das macht Offerten vergleichbar.",
      "Klären Sie, ob Abdecken und Möbelrücken im Preis enthalten sind.",
      "Bei Mietwohnungen lohnt sich ein Termin kurz vor der Abnahme.",
    ],
    faqs: [
      {
        question: "Was kostet es, eine Wohnung streichen zu lassen?",
        answer:
          "Für eine 3.5-Zimmer-Wohnung rechnen Sie in der Schweiz grob mit CHF 3'500 bis 7'000, je nach Zustand der Wände, Raumhöhe und Farbwahl.",
      },
      {
        question: "Wie lange dauern Malerarbeiten in einer Wohnung?",
        answer:
          "Eine durchschnittliche Wohnung ist meist in zwei bis vier Arbeitstagen fertig gestrichen.",
      },
      {
        question: "Muss ich beim Auszug die Wohnung streichen lassen?",
        answer:
          "Nur wenn die Abnutzung über das Normale hinausgeht oder es im Mietvertrag vereinbart ist. Normale Abnutzung ist durch die Miete abgegolten.",
      },
    ],
  },
  {
    slug: "sanitaer",
    name: "Sanitär & Heizung",
    headline: "Sanitär und Heizungsinstallateure finden",
    intro:
      "Vom tropfenden Wasserhahn über den Badumbau bis zum Ersatz der Ölheizung durch eine Wärmepumpe: Auf Begner finden Sie Installateure für Sanitär und Heizung in Ihrer Nähe.",
    services: [
      "Badumbau und Sanitärinstallationen",
      "Wärmepumpe installieren",
      "Heizungsersatz und Wartung",
      "Boiler ersetzen",
      "Leitungen und Abfluss",
    ],
    prices: [
      { label: "Boiler ersetzen", range: "CHF 2'500 bis 5'000" },
      { label: "Badumbau komplett", range: "CHF 25'000 bis 50'000" },
      { label: "Luft-Wasser-Wärmepumpe", range: "CHF 30'000 bis 50'000" },
    ],
    tips: [
      "Fragen Sie bei Heizungsersatz nach kantonalen Förderbeiträgen, bevor Sie unterschreiben.",
      "Fotos vom Heizraum und Boiler helfen bei einer ersten Einschätzung.",
      "Für Wärmepumpen braucht es oft eine Baubewilligung.",
    ],
    faqs: [
      {
        question: "Was kostet eine Wärmepumpe in der Schweiz?",
        answer:
          "Eine Luft-Wasser-Wärmepumpe für ein Einfamilienhaus kostet inklusive Installation grob CHF 30'000 bis 50'000, abzüglich allfälliger Förderbeiträge.",
      },
      {
        question: "Wie lange dauert ein Badumbau?",
        answer:
          "Ein kompletter Badumbau dauert in der Regel vier bis sechs Wochen, abhängig von Planung und Lieferzeiten.",
      },
      {
        question: "Gibt es Förderbeiträge für den Heizungsersatz?",
        answer:
          "Ja, viele Kantone und Gemeinden unterstützen den Ersatz fossiler Heizungen. Die Anträge müssen meist vor Baubeginn gestellt werden.",
      },
    ],
  },
  {
    slug: "elektriker",
    name: "Elektriker",
    headline: "Elektriker in der Schweiz finden",
    intro:
      "Neue Steckdosen, Lampen montieren, eine Photovoltaikanlage oder die Ladestation fürs Elektroauto: Beschreiben Sie Ihr Projekt und erhalten Sie Offerten von Elektroinstallateuren.",
    services: [
      "Elektroinstallationen",
      "Photovoltaik",
      "Ladestation für E-Auto",
      "Smart Home",
      "Sicherheitsnachweis (SiNa)",
    ],
    prices: [
      { label: "Stundenansatz Elektriker", range: "CHF 95 bis 130" },
      { label: "Ladestation inkl. Installation", range: "CHF 2'000 bis 4'500" },
      { label: "Photovoltaik Einfamilienhaus", range: "CHF 20'000 bis 35'000" },
    ],
    tips: [
      "Elektroarbeiten dürfen in der Schweiz nur konzessionierte Betriebe ausführen.",
      "Geben Sie Baujahr und Zustand der Installation an.",
      "Bei Photovoltaik lohnt sich ein Vergleich der Einspeisevergütung Ihres Energieversorgers.",
    ],
    faqs: [
      {
        question: "Was kostet ein Elektriker pro Stunde?",
        answer:
          "In der Schweiz liegen die Stundenansätze meist zwischen CHF 95 und 130, zuzüglich Anfahrt und Material.",
      },
      {
        question: "Darf ich Elektroarbeiten selbst machen?",
        answer:
          "Nur sehr einfache Arbeiten wie das Ersetzen von Lampen. Installationen gehören in die Hände eines konzessionierten Elektrikers.",
      },
      {
        question: "Was kostet eine Ladestation fürs Elektroauto?",
        answer:
          "Inklusive Installation rechnen Sie mit etwa CHF 2'000 bis 4'500, je nach Leitungslänge und Hausanschluss.",
      },
    ],
  },
  {
    slug: "bodenleger",
    name: "Bodenleger & Parkett",
    headline: "Bodenleger und Parkettleger finden",
    intro:
      "Parkett verlegen oder abschleifen, Vinyl, Laminat oder Platten: Auf Begner finden Sie Bodenleger, die Ihren neuen Boden sauber und termingerecht verlegen.",
    services: [
      "Parkett verlegen",
      "Parkett schleifen und versiegeln",
      "Vinyl und Laminat",
      "Teppich",
      "Plattenarbeiten",
    ],
    prices: [
      { label: "Parkett verlegen pro m² inkl. Material", range: "CHF 80 bis 160" },
      { label: "Parkett schleifen pro m²", range: "CHF 30 bis 50" },
      { label: "Vinyl verlegen pro m² inkl. Material", range: "CHF 60 bis 110" },
    ],
    tips: [
      "Geben Sie die Fläche in m² und den heutigen Bodenbelag an.",
      "Klären Sie, ob der alte Boden entfernt und entsorgt wird.",
      "Bei Bodenheizung eignen sich nicht alle Beläge.",
    ],
    faqs: [
      {
        question: "Was kostet neues Parkett pro m²?",
        answer:
          "Inklusive Material und Verlegung kostet Parkett in der Schweiz grob CHF 80 bis 160 pro m².",
      },
      {
        question: "Lohnt sich das Abschleifen von Parkett?",
        answer:
          "Bei Massivparkett oder ausreichend dicker Nutzschicht ja. Es kostet deutlich weniger als ein neuer Boden.",
      },
      {
        question: "Wie lange dauert das Verlegen eines Bodens?",
        answer:
          "Eine Wohnung ist meist in zwei bis fünf Tagen fertig, plus Trocknungszeit bei Versiegelung.",
      },
    ],
  },
  {
    slug: "dachdecker",
    name: "Dachdecker & Spengler",
    headline: "Dachdecker und Spengler finden",
    intro:
      "Undichtes Dach, neue Dachfenster oder eine energetische Dachsanierung: Beschreiben Sie Ihr Dachprojekt und vergleichen Sie Offerten von Dachdeckern und Spenglern.",
    services: [
      "Dachsanierung",
      "Dachisolation",
      "Dachfenster einbauen",
      "Dachrinnen und Spenglerarbeiten",
      "Reparaturen nach Sturm",
    ],
    prices: [
      { label: "Dachsanierung pro m²", range: "CHF 200 bis 400" },
      { label: "Dachfenster inkl. Einbau", range: "CHF 2'000 bis 4'000" },
      { label: "Dachrinne ersetzen pro Laufmeter", range: "CHF 60 bis 120" },
    ],
    tips: [
      "Energetische Dachsanierungen werden über das Gebäudeprogramm gefördert.",
      "Fotos von aussen und vom Estrich zeigen den Zustand am besten.",
      "Gerüstkosten sind oft ein grosser Posten, fragen Sie danach.",
    ],
    faqs: [
      {
        question: "Was kostet eine Dachsanierung?",
        answer:
          "Je nach Umfang und Isolation rechnen Sie grob mit CHF 200 bis 400 pro m² Dachfläche.",
      },
      {
        question: "Gibt es Förderung für die Dachisolation?",
        answer:
          "Ja, das Gebäudeprogramm von Bund und Kantonen unterstützt Wärmedämmungen. Das Gesuch muss vor Baubeginn eingereicht werden.",
      },
      {
        question: "Brauche ich für neue Dachfenster eine Bewilligung?",
        answer: "Häufig ja, besonders in Kernzonen. Klären Sie das mit Ihrer Gemeinde.",
      },
    ],
  },
  {
    slug: "schreiner",
    name: "Schreiner & Fenster",
    headline: "Schreiner und Fensterbauer finden",
    intro:
      "Neue Fenster, Innentüren, Einbauschränke oder eine Küche nach Mass: Auf Begner finden Sie Schreinereien und Fensterbauer aus Ihrer Region.",
    services: [
      "Fenster ersetzen",
      "Innentüren",
      "Einbauschränke",
      "Küchen nach Mass",
      "Reparaturen",
    ],
    prices: [
      { label: "Fenster ersetzen pro Stück", range: "CHF 1'200 bis 2'500" },
      { label: "Einbauschrank pro Laufmeter", range: "CHF 1'500 bis 3'000" },
      { label: "Innentür inkl. Montage", range: "CHF 900 bis 2'000" },
    ],
    tips: [
      "Notieren Sie Anzahl und ungefähre Masse der Fenster.",
      "Neue Fenster mit guter Dämmung werden teils gefördert.",
      "Fragen Sie nach Lieferfristen, Fenster brauchen oft mehrere Wochen.",
    ],
    faqs: [
      {
        question: "Was kostet ein neues Fenster?",
        answer:
          "Pro Fenster inklusive Montage rechnen Sie mit etwa CHF 1'200 bis 2'500, je nach Grösse und Verglasung.",
      },
      {
        question: "Wie lange dauert ein Fensterersatz?",
        answer:
          "Der Einbau selbst dauert pro Fenster oft nur wenige Stunden. Die Lieferzeit beträgt meist vier bis acht Wochen.",
      },
      {
        question: "Lohnt sich ein Einbauschrank nach Mass?",
        answer: "Bei Dachschrägen und Nischen ja, weil er den Raum optimal nutzt.",
      },
    ],
  },
  {
    slug: "gartenbau",
    name: "Gartenbau",
    headline: "Gartenbauer in der Schweiz finden",
    intro:
      "Gartenpflege, neuer Sitzplatz, Zaun oder Bepflanzung: Beschreiben Sie Ihr Gartenprojekt und erhalten Sie Offerten von Gartenbauern in der Nähe.",
    services: [
      "Gartenpflege",
      "Sitzplatz und Plattenarbeiten",
      "Zäune und Sichtschutz",
      "Rasen und Bepflanzung",
      "Baumschnitt",
    ],
    prices: [
      { label: "Stundenansatz Gärtner", range: "CHF 70 bis 100" },
      { label: "Sitzplatz mit Platten pro m²", range: "CHF 150 bis 300" },
      { label: "Rollrasen pro m² inkl. Verlegen", range: "CHF 25 bis 45" },
    ],
    tips: [
      "Eine Skizze mit Massen hilft bei der Offerte.",
      "Für regelmässige Pflege lohnt sich ein Jahresvertrag.",
      "Pflanzarbeiten im Frühling und Herbst planen.",
    ],
    faqs: [
      {
        question: "Was kostet ein Gärtner pro Stunde?",
        answer: "In der Schweiz liegen die Ansätze meist zwischen CHF 70 und 100 pro Stunde.",
      },
      {
        question: "Was kostet ein neuer Sitzplatz?",
        answer: "Inklusive Unterbau und Platten rechnen Sie grob mit CHF 150 bis 300 pro m².",
      },
      {
        question: "Wann ist die beste Zeit für Gartenbau?",
        answer: "Frühling und Herbst. Baumschnitt erfolgt meist im Winter.",
      },
    ],
  },
  {
    slug: "reinigung",
    name: "Umzug & Reinigung",
    headline: "Umzugsfirmen und Reinigung mit Abnahmegarantie",
    intro:
      "Umzug in eine neue Wohnung und Endreinigung mit Abnahmegarantie: Auf Begner vergleichen Sie Offerten von Umzugs- und Reinigungsfirmen.",
    services: [
      "Endreinigung mit Abnahmegarantie",
      "Privatumzug",
      "Möbellift",
      "Entsorgung und Räumung",
      "Unterhaltsreinigung",
      "Baureinigung und Bauendreinigung",
      "Grundreinigung nach Bauarbeiten",
    ],
    prices: [
      { label: "Endreinigung 3.5 Zimmer", range: "CHF 800 bis 1'400" },
      { label: "Umzug 3.5 Zimmer regional", range: "CHF 1'500 bis 3'000" },
      { label: "Räumung pro m³", range: "CHF 80 bis 150" },
    ],
    tips: [
      "Achten Sie auf eine schriftliche Abnahmegarantie.",
      "Geben Sie Stockwerk und Lift an, das beeinflusst den Preis stark.",
      "Monatsende ist teurer, ein Termin unter dem Monat spart oft Geld.",
    ],
    faqs: [
      {
        question: "Was kostet eine Endreinigung mit Abnahmegarantie?",
        answer: "Für eine 3.5-Zimmer-Wohnung rechnen Sie mit etwa CHF 800 bis 1'400.",
      },
      {
        question: "Was bedeutet Abnahmegarantie?",
        answer:
          "Die Firma reinigt kostenlos nach, falls die Verwaltung bei der Wohnungsabgabe etwas beanstandet.",
      },
      {
        question: "Wann sollte ich die Umzugsfirma buchen?",
        answer: "Idealerweise vier bis acht Wochen vorher, besonders für Termine am Monatsende.",
      },
    ],
  },
];

export function getTradePage(slug: string) {
  return tradePages.find((trade) => trade.slug === slug);
}
